import { PlusIcon } from '@heroicons/react/24/solid';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
} from '@mui/joy';
import Button from '@mui/joy/Button';

import { Fragment, useId, useState } from 'react';

import { useCreateFeedbackConfigMutation } from '@/hooks/useSwr';
import useToastOn429Error from '@/hooks/useToastOn429Error';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import { FeedbackConfigCreateBody, FeedbackConfigSchema } from '@/types/schema';
import { getColorByString } from '@/utils/get-color-by-string';

import { ExpandableErrorAlert } from '../ExpandableErrorAlert';

type FilterUnion<T, U> = T extends U ? T : never;
type TaggedFeedbackConfig<
  Key extends FeedbackConfigSchema['feedback_config']['type']
> = FilterUnion<FeedbackConfigSchema['feedback_config'], { type: Key }>;

function FeedbackConfigCategorical(props: {
  value: TaggedFeedbackConfig<'categorical'>;
  errors: Record<string, string>;
  onChange: (value: TaggedFeedbackConfig<'categorical'>) => void;
}) {
  const id = useId();
  const categories =
    'categories' in props.value && Array.isArray(props.value.categories)
      ? props.value.categories
      : [];

  return (
    <div className="flex flex-col gap-1.5">
      <label id={id} className="text-sm font-medium">
        Categories
      </label>

      <div className="flex flex-col gap-2">
        {categories.map((category, idx) => {
          function onChange(diff: Partial<{ label?: string; value: number }>) {
            const newCategories = [...categories];
            newCategories[idx] = { ...newCategories[idx], ...diff };
            props.onChange({
              type: 'categorical',
              categories: newCategories,
            });
          }

          return (
            <Fragment key={idx}>
              <div className="flex items-center">
                <Input
                  error={!!props.errors[`categories.${idx}.label`]}
                  type="text"
                  placeholder="Category Label"
                  className="min-w-0"
                  value={category.label ?? ''}
                  onChange={(e) =>
                    onChange({ label: e.target.value || undefined })
                  }
                />

                <span className="w-2 flex-shrink-0" />

                <Input
                  error={!!props.errors[`categories.${idx}.value`]}
                  type="number"
                  className="min-w-0"
                  value={category.value}
                  onChange={(e) => onChange({ value: +e.target.value })}
                />

                <span className="w-1 flex-shrink-0" />

                <button
                  type="button"
                  className="rounded p-2 outline-none transition-colors focus-within:bg-secondary hover:bg-secondary-hover active:bg-secondary disabled:bg-transparent disabled:opacity-50"
                  onClick={() => {
                    const newCategories = [...categories];
                    newCategories.splice(idx, 1);
                    props.onChange({
                      type: 'categorical',
                      categories: newCategories,
                    });
                  }}
                  aria-label="Remove"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              {props.errors[`categories.${idx}.label`] && (
                <span className="text-sm text-error">
                  {props.errors[`categories.${idx}.label`]}
                </span>
              )}

              {props.errors[`categories.${idx}.value`] && (
                <span className="text-sm text-error">
                  {props.errors[`categories.${idx}.value`]}
                </span>
              )}
            </Fragment>
          );
        })}
        {props.errors.categories && (
          <span className="text-sm text-error">{props.errors.categories}</span>
        )}
      </div>

      <Button
        type="button"
        variant="outlined"
        color="neutral"
        startDecorator={<PlusIcon className="h-5 w-5" />}
        onClick={() => {
          props.onChange({
            type: 'categorical',
            categories: [
              ...categories,
              { label: undefined, value: categories.length },
            ],
          });
        }}
      >
        Add category
      </Button>
    </div>
  );
}

function FeedbackConfigContinuous(props: {
  value: TaggedFeedbackConfig<'continuous'>;
  errors: Record<string, string>;
  onChange: (value: TaggedFeedbackConfig<'continuous'>) => void;
}) {
  const min =
    'min' in props.value && typeof props.value.min === 'number'
      ? props.value.min
      : null;

  const max =
    'max' in props.value && typeof props.value.max === 'number'
      ? props.value.max
      : null;
  return (
    <>
      <FormControl error={!!props.errors.min}>
        <FormLabel>Minimum</FormLabel>
        <Input
          type="number"
          value={min ?? ''}
          onChange={(e) => {
            const value = +e.target.value;
            if (e.target.value === '') {
              props.onChange({ type: 'continuous', max, min: null });
            } else if (!Number.isNaN(value)) {
              props.onChange({ type: 'continuous', max, min: value });
            }
          }}
          sx={{
            '& input': {
              boxShadow: 'none !important',
              MozAppearance: 'textfield',
            },
            '& input::-webkit-outer-spin-button': {
              WebkitAppearance: 'none !important',
              margin: 0,
            },
            '& input::-webkit-inner-spin-button': {
              WebkitAppearance: 'none !important',
              margin: 0,
            },
          }}
        />
        {props.errors.min && (
          <FormHelperText>{props.errors.min}</FormHelperText>
        )}
      </FormControl>

      <FormControl error={!!props.errors.max}>
        <FormLabel>Maximum</FormLabel>
        <Input
          type="number"
          data-testid="feedback-config-continuous-max"
          value={max ?? ''}
          onChange={(e) => {
            const value = +e.target.value;

            if (e.target.value === '') {
              props.onChange({ type: 'continuous', min, max: null });
            } else if (!Number.isNaN(value)) {
              props.onChange({ type: 'continuous', min, max: value });
            }
          }}
          sx={{
            '& input': {
              boxShadow: 'none !important',
              MozAppearance: 'textfield',
            },
            '& input::-webkit-outer-spin-button': {
              WebkitAppearance: 'none !important',
              margin: 0,
            },
            '& input::-webkit-inner-spin-button': {
              WebkitAppearance: 'none !important',
              margin: 0,
            },
          }}
        />
        {props.errors.max && (
          <FormHelperText>{props.errors.max}</FormHelperText>
        )}
      </FormControl>
    </>
  );
}

export function FeedbackConfigForm(props: {
  onSuccess?: (data: FeedbackConfigSchema) => void;
  defaultTagText?: string;
}) {
  const create = useCreateFeedbackConfigMutation({
    onSuccess: props.onSuccess,
  });

  const { handle429Error, is429Error } = useToastOn429Error();

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [value, setValue] = useState<FeedbackConfigCreateBody>({
    feedback_key: props.defaultTagText ?? '',
    feedback_config: { type: 'continuous', min: 0, max: 1 },
  });

  const type = value.feedback_config.type;

  const is429 = is429Error(create.error);

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (e) => {
        if (create.isMutating) return;

        e.preventDefault();
        e.stopPropagation();

        const newErrors: Record<string, string> = {};
        if (value.feedback_key === '') {
          newErrors['feedback_key'] = 'Feedback tag is required';
        }

        if (value.feedback_config.type === 'categorical') {
          if (
            value.feedback_config.categories == null ||
            value.feedback_config.categories.length <= 1
          ) {
            newErrors['feedback_config.categories'] =
              'At least 2 categories are required';
          } else {
            const uniqueValues = new Set<number>();
            const uniqueLabels = new Set<string>();

            for (
              let idx = 0;
              idx < value.feedback_config.categories.length;
              idx++
            ) {
              const category = value.feedback_config.categories[idx];

              if (category.value == null) {
                newErrors[`feedback_config.categories.${idx}.value`] =
                  'Category value is required';
              } else {
                if (uniqueValues.has(category.value)) {
                  newErrors[`feedback_config.categories.${idx}.value`] =
                    'Category value must be unique';
                }
                uniqueValues.add(category.value);
              }

              if (category.label) {
                if (uniqueLabels.has(category.label)) {
                  newErrors[`feedback_config.categories.${idx}.label`] =
                    'Category label must be unique';
                }
                uniqueLabels.add(category.label);
              }
            }
          }
        }

        if (value.feedback_config.type === 'continuous') {
          if (
            value.feedback_config.max != null &&
            value.feedback_config.min != null &&
            value.feedback_config.min >= value.feedback_config.max
          ) {
            newErrors['feedback_config.max'] =
              'Maximum must be greater than minimum';
          }
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
          try {
            await create.trigger({ json: value });
          } catch (e: any) {
            handle429Error(e, 'feedback');
          }
        }
      }}
    >
      <FormControl error={!!errors.feedback_key}>
        <FormLabel>Feedback Tag</FormLabel>

        <Input
          value={value.feedback_key}
          autoFocus
          startDecorator={
            <span
              className="h-2 w-2 rounded-sm"
              style={{ background: getColorByString(value.feedback_key) }}
            />
          }
          placeholder="Enter feedback tag"
          onChange={(e) => {
            const feedback_key = e.target.value;
            setValue({ ...value, feedback_key });
          }}
          sx={{ '& input': { boxShadow: 'none !important' } }}
        />

        {errors.feedback_key && (
          <FormHelperText>{errors.feedback_key}</FormHelperText>
        )}
      </FormControl>

      <FormControl>
        <FormLabel>Type</FormLabel>
        <Select
          value={type}
          onChange={(_, type) => {
            if (type != null) {
              const feedback_config: FeedbackConfigCreateBody['feedback_config'] =
                { type };

              if (type === 'categorical') {
                feedback_config.categories = [
                  { label: undefined, value: 0 },
                  { label: undefined, value: 1 },
                ];
                feedback_config.min = null;
                feedback_config.max = null;
              }

              if (type === 'continuous') {
                feedback_config.categories = null;
                feedback_config.min = 0;
                feedback_config.max = 1;
              }

              if (type === 'freeform') {
                feedback_config.categories = null;
                feedback_config.min = null;
                feedback_config.max = null;
              }

              setValue({ ...value, feedback_config });
            }
          }}
        >
          <Option value="categorical">Categorical</Option>
          <Option value="continuous">Continuous</Option>
          <Option value="freeform">Freeform</Option>
        </Select>
      </FormControl>

      {type === 'categorical' && (
        <FeedbackConfigCategorical
          value={value.feedback_config}
          errors={Object.fromEntries(
            Object.entries(errors)
              .filter(([k]) => k.startsWith('feedback_config.'))
              .map(([k, v]) => [k.slice('feedback_config.'.length), v])
          )}
          onChange={(feedback_config) =>
            setValue({ ...value, feedback_config })
          }
        />
      )}

      {type === 'continuous' && (
        <FeedbackConfigContinuous
          value={value.feedback_config}
          errors={Object.fromEntries(
            Object.entries(errors)
              .filter(([k]) => k.startsWith('feedback_config.'))
              .map(([k, v]) => [k.slice('feedback_config.'.length), v])
          )}
          onChange={(feedback_config) =>
            setValue({ ...value, feedback_config })
          }
        />
      )}

      {create.error && !is429 && <ExpandableErrorAlert error={create.error} />}

      <Button
        type="submit"
        variant="solid"
        color="primary"
        loading={create.isMutating}
      >
        Save
      </Button>
    </form>
  );
}
